import { company } from '~/company/Company';
import type { RatingData } from '~/pages/Product/Citydrinks/ProductV3/components/ProductRatingSummary/interfaces';
import type { ProductState } from '~/stores/CategoriesStore/ProductState';

const STARS = [1, 2, 3, 4, 5] as const;

type Star = (typeof STARS)[number];

/**
 * Rating summary of the product page review block. `null` when the company
 * has no reviews on the product page or nothing has been rated yet.
 *
 * The catalog API is the source of truth for the histogram, but not every
 * company fills it. Without a fallback the whole review section would be
 * hidden even though reviews exist, so the counts are then derived from the
 * loaded reviews instead.
 */
export const getProductRatingData = (
  product: ProductState,
): RatingData | null => {
  if (!company.useReviewsOnProductPage) {
    return null;
  }

  const histogram = product.data?.ratingHistogram;

  const ratingCounts: Record<Star, number> = histogram
    ? {
        5: histogram['5'] ?? 0,
        4: histogram['4'] ?? 0,
        3: histogram['3'] ?? 0,
        2: histogram['2'] ?? 0,
        1: histogram['1'] ?? 0,
      }
    : product.reviews.list.reduce(
        (acc, review) => {
          const mark = Math.min(
            5,
            Math.max(1, Math.round(review.mark)),
          ) as Star;

          acc[mark] += 1;

          return acc;
        },
        { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } as Record<Star, number>,
      );

  const totalRatings = STARS.reduce((sum, star) => sum + ratingCounts[star], 0);
  if (totalRatings === 0) {
    return null;
  }

  const averageRating =
    product.data?.ratingAverage ||
    Number(
      (
        STARS.reduce((sum, star) => sum + star * ratingCounts[star], 0) /
        totalRatings
      ).toFixed(1),
    );

  const toPercentage = (count: number) =>
    Number(((count / totalRatings) * 100).toFixed(1));

  return {
    averageRating,
    totalRatings,
    distribution: ([5, 4, 3, 2, 1] as const).map((stars) => ({
      stars,
      count: ratingCounts[stars],
      percentage: toPercentage(ratingCounts[stars]),
    })),
  };
};
